import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Grid,
  Paper,
  Chip,
  Button
} from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const FourPillarsResult = ({ result }) => {
  const [periodicFortune, setPeriodicFortune] = useState(null);
  const [loadingType, setLoadingType] = useState(null);
  const [error, setError] = useState(null);
  const [periodicFortuneType, setPeriodicFortuneType] = useState(null);

  if (!result || !result.pillars) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          占い結果が正しく取得できませんでした。
        </Typography>
      </Box>
    );
  }

  const { pillars, traits, inputData = {}, elements, fortunes } = result;

  const fetchFortune = async (type) => {
    setLoadingType(type);
    setError(null);
    try {
      const res = await axios.post(`${API_URL}/api/fortune/${type}`, inputData);
      setPeriodicFortune(res.data.result);
	  setPeriodicFortuneType(type);
    } catch (err) {
      setError('運勢の取得に失敗しました。');
      console.error(err);
    } finally {
      setLoadingType(null);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        あなたの四柱推命
      </Typography>

      {/* 入力情報 */}
      {inputData && inputData.year && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
            mb: 3,
            p: 2,
            backgroundColor: '#FAFAFA',
            borderRadius: 1
          }}
        >
          <Typography variant="subtitle2" sx={{ width: '100%', mb: 1, color: 'text.secondary', fontSize: '1.2rem' }}>
            ＊入力情報＊
          </Typography>
          <Chip label={`${inputData.year}年`} size="small" sx={{ backgroundColor: '#F9F4F5', fontSize: '1.2rem' }} />
          <Chip label={`${inputData.month}月`} size="small" sx={{ backgroundColor: '#EFF7F6', fontSize: '1.2rem' }} />
          <Chip label={`${inputData.day}日`} size="small" sx={{ backgroundColor: '#F4F3E7', fontSize: '1.2rem' }} />
          <Chip label={`${inputData.hour.toString().padStart(2, '0')}時`} size="small" sx={{ backgroundColor: '#F0F5E9', fontSize: '1.2rem' }} />
        </Box>
      )}

      {/* 命式 */}
      <Grid container spacing={2} sx={{ mb: 4, mt: 2 }}>
        {['year', 'month', 'day', 'hour'].map((key, i) => (
          <Grid item xs={3} key={key}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: 'center',
                backgroundColor: ['#F9F4F5', '#EFF7F6', '#F4F3E7', '#F0F5E9'][i],
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {['年柱', '月柱', '日柱', '時柱'][i]}
              </Typography>
              <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
                {pillars[`${key}Pillar`]}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pillars[`${key}Animal`]}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* 性格特性 */}
      <Box>
        <Typography variant="h6" component="h3" gutterBottom>
          ■あなたの性格特性■
        </Typography>
        <Box sx={{ mt: 2 }}>
          {Array.isArray(traits) ? (
            traits.map((trait, index) => (
              <Box
                key={index}
                sx={{
                  mb: 1.5,
                  p: 2,
                  backgroundColor: index % 2 === 0 ? '#F8F9FA' : 'white',
                  borderRadius: 1
                }}
              >
                <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                  • {trait}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              性格分析が取得できませんでした。
            </Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* 今週・今月の運勢ボタン */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" gutterBottom>🔮 今週・今月の運勢</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => fetchFortune('weekly')}
            disabled={loadingType === 'weekly'}
          >
            {loadingType === 'weekly' ? '取得中...' : '今週の運勢'}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => fetchFortune('monthly')}
            disabled={loadingType === 'monthly'}
          >
            {loadingType === 'monthly' ? '取得中...' : '今月の運勢'}
          </Button>
        </Box>

        {error && (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        )}

		{periodicFortune && (
			<Box sx={{ mt: 2, p: 2, backgroundColor: '#FAFAFA', borderRadius: 1 }}>
		       <Typography variant="h6" gutterBottom>
                 🔮 {periodicFortuneType === 'weekly' ? '今週の運勢' : '今月の運勢'}
               </Typography>

				{['love', 'money', 'work'].map((key, i) => {
					const label = key === 'love' ? '❤️ 恋愛運'
								: key === 'money' ? '💰 金銭運'
								: '💼 仕事運';
					const text = periodicFortune[key] || '';
					const [first, ...rest] = text.split('。');
					const second = rest.join('。'); // 句点を戻して後半をまとめる

					return (
						<Box key={key} sx={{ mb: 3 }}>
							<Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 1,  fontSize: '1.2rem'  }}>{label}</Typography>
							<Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1,  fontSize: '1.2rem'  }}>
								{first}。
							</Typography>
							<Typography variant="body1" sx={{ mb: 1,  fontSize: '1.2rem'  }}>
								{second}
							</Typography>
						</Box>
					);
				})}
			</Box>
		)}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* 五行運勢 */}
      <Box>
        <Typography variant="h6" component="h3" gutterBottom>
          ■あなたの強い要素: {elements?.strongest}
        </Typography>
        {elements?.weakest && (
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            ■あなたの弱い要素: {elements.weakest}
          </Typography>
        )}
        {/* fortunes 既存の内容はそのまま */}
      </Box>

      {/* 補足メッセージ */}
      <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          ＊AIが提案をしています＊<br />
          ＊評価は各人でお願いします。＊
        </Typography>
      </Box>
    </Box>
  );
};

export default FourPillarsResult;

